import { useRef, useState, useEffect } from "react";
import { Lightbulb, Send, CheckCircle, AlertCircle, ChevronDown, Loader2 } from "lucide-react";

// Custom hook for intersection observer (iOS-safe)
const useIsVisible = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      }
    }, { threshold: options.amount || 0.3 });
    
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, options.once, options.amount]);
  
  return isVisible;
};

const categories = [
  { value: "feature", label: "New Feature", icon: "✨" },
  { value: "improvement", label: "Improvement", icon: "🔧" },
  { value: "bot", label: "RYS Bot", icon: "🤖" },
  { value: "staking", label: "Staking", icon: "💰" },
  { value: "ui", label: "UI/UX", icon: "🎨" },
  { value: "other", label: "Other", icon: "💬" },
];

export default function Suggestions() {
  const sectionRef = useRef(null);
  const isVisible = useIsVisible(sectionRef, { once: true, amount: 0.2 });
  
  const [formData, setFormData] = useState({
    category: "",
    title: "",
    description: "",
    contact: "", // optional email/telegram
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategorySelect = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
    setDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.title || !formData.description) {
      setSubmitStatus("error");
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch("/project/api/suggestion", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          category: formData.category,
          title: formData.title,
          description: formData.description,
          contact: formData.contact || "",
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus("success");
        setFormData({ category: "", title: "", description: "", contact: "" });
      } else {
        setSubmitStatus("error");
        setErrorMessage(data.message || "Failed to submit suggestion.");
      }
    } catch (error) {
      console.error("Suggestion submission error:", error);
      setSubmitStatus("error");
      setErrorMessage("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCategory = categories.find(c => c.value === formData.category);

  return (
    <section 
      id="suggestions" 
      ref={sectionRef}
      className="relative py-24 bg-[#0b0b0f] border-t border-white/5 overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(168,85,247,0.08),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(59,130,246,0.06),transparent_50%)]" />
      
      <div className="relative max-w-2xl mx-auto px-6">
        {/* Header */}
        <div 
          className={`text-center mb-10 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
            <Lightbulb className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-300 font-medium">Community Driven</span>
          </div>
          
          <h3 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
            Share Your Ideas
          </h3>
          <p className="text-gray-400 max-w-lg mx-auto">
            Help shape the future of RefundYourSOL. Your suggestions drive our development priorities.
          </p>
        </div>

        {/* Form Card */}
        <div 
          className={`glass rounded-2xl p-6 md:p-8 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {submitStatus === "success" ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-2">Thank You!</h4>
              <p className="text-gray-400 mb-6">
                Your suggestion has been submitted. We review all ideas and prioritize based on community demand.
              </p>
              <button
                onClick={() => setSubmitStatus(null)}
                className="px-6 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors"
              >
                Submit Another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Category Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category <span className="text-purple-400">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-left flex items-center justify-between hover:border-purple-500/50 transition-colors focus:outline-none focus:border-purple-500"
                  >
                    {selectedCategory ? (
                      <span className="text-white">
                        {selectedCategory.icon} {selectedCategory.label}
                      </span>
                    ) : (
                      <span className="text-gray-500">Select a category...</span>
                    )}
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 py-2 rounded-xl bg-[#1a1a24] border border-white/10 shadow-xl z-20">
                      {categories.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => handleCategorySelect(cat.value)}
                          className={`w-full px-4 py-2.5 text-left hover:bg-white/5 transition-colors flex items-center gap-2 ${
                            formData.category === cat.value ? 'bg-purple-500/10 text-purple-300' : 'text-gray-300'
                          }`}
                        >
                          <span>{cat.icon}</span>
                          <span>{cat.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title <span className="text-purple-400">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Brief summary of your idea..."
                  maxLength={100}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description <span className="text-purple-400">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your suggestion in detail. What problem does it solve? How would it work?"
                  rows={5}
                  maxLength={2000}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {formData.description.length}/2000
                </p>
              </div>

              {/* Contact (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contact <span className="text-gray-500">(optional)</span>
                </label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  placeholder="Email or Telegram @username for follow-up"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>

              {/* Error Message */}
              {submitStatus === "error" && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-300">{errorMessage}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Suggestion
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                All suggestions are reviewed by the team. Popular ideas get prioritized.
              </p>
            </form>
          )}
        </div>

        {/* Bottom note */}
        <p 
          className={`text-center text-gray-500 text-sm mt-8 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          Want to discuss ideas first? Join our{" "}
          <a 
            href="https://t.me/refundyoursolcom" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-purple-400 hover:text-purple-300 transition-colors"
          >
            Telegram community
          </a>
        </p>
      </div>
    </section>
  );
}

